import { safeMessage, tidy } from "./shared/safe";

// Pure logic for the bulk client import screen: reading what the owner pasted or chose (JSON or CSV), the request body,
// reading the server's untrusted reply, and the plain-words plan that the Confirm button states. No DOM, no network.
//
// Contract: POST /v1/clients/import (docs/build/handoffs/worker-a-routes.md s3.1). The client key is the slug. A dry run is
// always 200 and writes nothing; the default onError "abort" applies nothing and answers 422 when any row is invalid.

export const MAX_ROWS = 200;
export const TOO_MANY_MESSAGE = "At most 200 rows per import request; split the file.";

/** Fact fields a client row may carry (worker-a-routes.md s3.2) plus the key. Anything else is reported, then left to the server. */
export const KNOWN_FIELDS = [
  "slug", "business_name", "legal_name", "status", "primary_category", "niche", "address", "city", "state", "zip", "phone", "email",
  "hours_json", "owner_name", "owner_role", "founding_year", "services_json", "service_area_json", "money_site_url", "money_pages_json",
  "claim_block", "attributes_json", "target_rules_json", "voice_profile_json", "gbp_location_id", "podcast_owner_email", "template_selection_json",
] as const;
const KNOWN = new Set<string>(KNOWN_FIELDS);

/** Documented header aliases (shown to the owner). Compared after lower-casing and turning spaces/dashes into underscores. */
export const FIELD_ALIASES: Readonly<Record<string, string>> = {
  name: "business_name",
  business: "business_name",
  company: "business_name",
  website: "money_site_url",
  url: "money_site_url",
  money_site: "money_site_url",
  category: "primary_category",
  postcode: "zip",
  postal_code: "zip",
  zip_code: "zip",
  telephone: "phone",
  owner: "owner_name",
};

/** The server refuses these with POLICY_FIELD (they are owner-only settings); the page never sends them. */
export const POLICY_FIELDS = ["mode", "monthly_ai_budget_cents"] as const;
const POLICY = new Set<string>(POLICY_FIELDS);

export type ImportRow = Record<string, unknown>;

export interface ParseIssue {
  /** 1-based data row (JSON: array position; CSV: record number after the header), or null for a whole-input problem. */
  row: number | null;
  /** CSV only: the physical line where the record starts. */
  line: number | null;
  message: string;
}

export interface ParsedInput {
  format: "json" | "csv" | "empty";
  rows: ImportRow[];
  issues: ParseIssue[];
  /** Input columns/keys that are not a known fact field (after aliases): sent as written; the server decides and says what it thinks. */
  unknownFields: string[];
  aliasesUsed: { from: string; to: string }[];
  /** Policy fields found in the input and removed before sending. */
  strippedPolicy: string[];
  tooMany: boolean;
}

const emptyParsed = (): ParsedInput => ({ format: "empty", rows: [], issues: [], unknownFields: [], aliasesUsed: [], strippedPolicy: [], tooMany: false });

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const hasOwn = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);
const normalizeKey = (k: string) => k.trim().replace(/^\uFEFF/, "").toLowerCase().replace(/[\s-]+/g, "_");

/** Maps one input key to the API field name. Returns the key to use and whether an alias applied. */
function mapKey(raw: string): { key: string; alias: boolean } {
  const n = normalizeKey(raw);
  if (hasOwn(FIELD_ALIASES, n)) return { key: FIELD_ALIASES[n], alias: true };
  return { key: n, alias: false };
}

/** Builds the rows' shared bookkeeping: aliases, unknown fields and stripped policy fields. */
class Collector {
  unknown = new Set<string>();
  aliases = new Map<string, string>();
  stripped = new Set<string>();
  issues: ParseIssue[] = [];

  /** Applies aliases and the policy strip to one record. `where` identifies the row in messages. */
  row(entries: [string, unknown][], where: { row: number; line: number | null }): ImportRow {
    // A null-prototype object keeps odd keys such as "__proto__" as plain data (they are then sent as plain JSON properties).
    const out: ImportRow = Object.create(null);
    for (const [rawKey, value] of entries) {
      const { key, alias } = mapKey(rawKey);
      if (key === "") continue;
      if (POLICY.has(key)) {
        this.stripped.add(key);
        continue;
      }
      if (alias) this.aliases.set(normalizeKey(rawKey), key);
      if (hasOwn(out, key)) {
        this.issues.push({ row: where.row, line: where.line, message: `"${rawKey}" and another column both mean "${key}"; the first one is used.` });
        continue;
      }
      if (!KNOWN.has(key)) this.unknown.add(rawKey.trim());
      out[key] = value;
    }
    return out;
  }
}

// ---- JSON ------------------------------------------------------------------------------------------------------

function parseJson(text: string): ParsedInput {
  const out = emptyParsed();
  out.format = "json";
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (e) {
    out.issues.push({ row: null, line: null, message: `That is not valid JSON: ${safeMessage((e as Error).message, 160)}` });
    return out;
  }
  const list = Array.isArray(value) ? value : isObject(value) && Array.isArray(value.clients) ? value.clients : null;
  if (list === null) {
    out.issues.push({ row: null, line: null, message: 'JSON must be an array of client objects, or an object like {"clients": [ ... ]}.' });
    return out;
  }
  const c = new Collector();
  list.forEach((item, i) => {
    if (!isObject(item)) {
      c.issues.push({ row: i + 1, line: null, message: "This entry is not an object, so it was left out." });
      return;
    }
    out.rows.push(c.row(Object.entries(item), { row: i + 1, line: null }));
  });
  return finish(out, c);
}

// ---- CSV (RFC 4180) --------------------------------------------------------------------------------------------

interface CsvRecord {
  cells: string[];
  line: number;
}

/** Quoted fields may hold commas, newlines and doubled quotes. Returns the records and a problem if a quote never closes. */
function csvRecords(text: string): { records: CsvRecord[]; unterminatedAtLine: number | null } {
  const records: CsvRecord[] = [];
  let cells: string[] = [];
  let cell = "";
  let inQuotes = false;
  let line = 1;
  let recordLine = 1;
  let quoteLine = 1;
  let touched = false; // anything seen in this record, so a lone blank line can be skipped
  const endCell = () => {
    cells.push(cell);
    cell = "";
  };
  const endRecord = () => {
    endCell();
    if (!(cells.length === 1 && cells[0] === "" && !touched)) records.push({ cells, line: recordLine });
    cells = [];
    touched = false;
  };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else inQuotes = false;
      } else {
        if (ch === "\n") line++;
        cell += ch;
      }
      continue;
    }
    if (ch === '"' && cell === "") {
      inQuotes = true;
      quoteLine = line;
      touched = true;
    } else if (ch === ",") {
      touched = true;
      endCell();
    } else if (ch === "\r" || ch === "\n") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      endRecord();
      line++;
      recordLine = line;
    } else {
      touched = true;
      cell += ch;
    }
  }
  if (inQuotes) return { records, unterminatedAtLine: quoteLine };
  if (cell !== "" || cells.length > 0 || touched) endRecord();
  return { records, unterminatedAtLine: null };
}

function parseCsv(text: string): ParsedInput {
  const out = emptyParsed();
  out.format = "csv";
  const parsedRecords = csvRecords(text);
  const unterminatedAtLine = parsedRecords.unterminatedAtLine;
  const records = parsedRecords.records.filter((r) => r.cells.some((x) => x.trim() !== "")); // blank (or whitespace-only) lines are ignored
  if (unterminatedAtLine !== null) {
    out.issues.push({ row: null, line: unterminatedAtLine, message: `A quoted value that starts on line ${unterminatedAtLine} never ends. Nothing can be read until the quote is closed.` });
    return out;
  }
  if (records.length === 0) {
    out.format = "empty";
    return out;
  }
  const header = records[0].cells.map((h) => h.trim());
  const c = new Collector();
  const seen = new Map<string, number>();
  header.forEach((raw, i) => {
    const { key } = mapKey(raw);
    if (key === "") c.issues.push({ row: null, line: records[0].line, message: `Column ${i + 1} of the header row has no name.` });
    else if (seen.has(key)) c.issues.push({ row: null, line: records[0].line, message: `The header names "${key}" twice (columns ${seen.get(key)! + 1} and ${i + 1}).` });
    else seen.set(key, i);
  });
  if (records.length === 1) c.issues.push({ row: null, line: null, message: "There is a header row but no client rows." });
  records.slice(1).forEach((rec, i) => {
    const row = i + 1;
    if (rec.cells.length > header.length) {
      c.issues.push({ row, line: rec.line, message: `This row has ${rec.cells.length} values but the header has ${header.length} columns. Quote values that contain commas.` });
      return;
    }
    const entries: [string, unknown][] = [];
    header.forEach((name, col) => {
      const v = (rec.cells[col] ?? "").trim();
      if (v !== "" && name !== "") entries.push([name, v]); // an empty cell is "not provided", not an empty string
    });
    out.rows.push(c.row(entries, { row, line: rec.line }));
  });
  return finish(out, c);
}

function finish(out: ParsedInput, c: Collector): ParsedInput {
  out.issues.push(...c.issues);
  out.unknownFields = [...c.unknown].sort();
  out.aliasesUsed = [...c.aliases].map(([from, to]) => ({ from, to })).sort((a, b) => a.from.localeCompare(b.from));
  out.strippedPolicy = [...c.stripped].sort();
  out.tooMany = out.rows.length > MAX_ROWS;
  if (out.tooMany) out.issues.push({ row: null, line: null, message: `${out.rows.length} rows. ${TOO_MANY_MESSAGE}` });
  // Every row is checked by the server; the page only reports a missing key early so the owner is not surprised.
  return out;
}

/** JSON when the text starts with [ or {, otherwise CSV. */
export function parseInput(text: string): ParsedInput {
  const t = text.replace(/^\uFEFF/, "");
  if (t.trim() === "") return emptyParsed();
  const first = t.trimStart()[0];
  return first === "[" || first === "{" ? parseJson(t) : parseCsv(t);
}

/** True when the dry run may be started: something parsed, nothing malformed, within the cap. */
export const canDryRun = (p: ParsedInput): boolean => p.rows.length > 0 && p.issues.length === 0 && !p.tooMany;

// ---- request ---------------------------------------------------------------------------------------------------

export type OnExisting = "report" | "update";
export type OnError = "abort" | "skip";
export interface ImportOptions {
  onExisting: OnExisting;
  onError: OnError;
}
export const DEFAULT_OPTIONS: ImportOptions = { onExisting: "report", onError: "abort" };

export interface ImportBody {
  clients: ImportRow[];
  dryRun: boolean;
  onExisting: OnExisting;
  onError: OnError;
}

export function buildBody(rows: ImportRow[], options: ImportOptions, dryRun: boolean): ImportBody {
  return { clients: rows, dryRun, onExisting: options.onExisting, onError: options.onError };
}

/** Identifies exactly what a dry run covered: the rows and the options (not dryRun). A confirm is allowed only for the same key. */
export function requestKey(rows: ImportRow[], options: ImportOptions): string {
  return JSON.stringify([rows, options.onExisting, options.onError]);
}

// ---- reply -----------------------------------------------------------------------------------------------------

export const ACTIONS = ["create", "update", "unchanged", "exists", "error", "skipped"] as const;
export type RowAction = (typeof ACTIONS)[number] | "unknown";

export const ACTION_LABELS: Record<RowAction, string> = {
  create: "Will create",
  update: "Will update",
  unchanged: "Unchanged",
  exists: "Exists with different values (not changed)",
  error: "Error",
  skipped: "Skipped",
  unknown: "Unrecognised",
};
/** After the real import the same actions are in the past tense. */
export const ACTION_LABELS_DONE: Record<RowAction, string> = {
  create: "Created",
  update: "Updated",
  unchanged: "Unchanged",
  exists: "Exists with different values (not changed)",
  error: "Error",
  skipped: "Skipped",
  unknown: "Unrecognised",
};
export const actionTone = (a: RowAction): "ok" | "warn" | "bad" | "neutral" => (a === "create" || a === "update" ? "ok" : a === "error" ? "bad" : a === "exists" || a === "skipped" || a === "unknown" ? "warn" : "neutral");

export interface RowIssue {
  field: string;
  code: string;
  message: string;
}
export interface DiffEntry {
  field: string;
  before: string;
  after: string;
}
export interface ResultRow {
  /** 0-based, as the server sends it. Shown to people as index + 1. */
  index: number;
  slug: string;
  action: RowAction;
  /** What the row would do if nothing blocked the write (current servers; under "stop if any row is invalid" a blocked row's action is "skipped"). */
  plannedAction: RowAction | null;
  errors: RowIssue[];
  diff: DiffEntry[];
  clientId: string | null;
}
export interface Summary {
  total: number;
  create: number;
  update: number;
  unchanged: number;
  exists: number;
  error: number;
  skipped: number;
}
/** `confirm` of a current server: what a confirm with exactly this body does (or, for a real request, did). Absent on older servers. */
export interface ConfirmInfo {
  wouldApply: boolean;
  wouldAbort: boolean;
  wouldWrite: { create: number; update: number };
  message: string | null;
  caveats: string[];
}
export interface ImportReply {
  dryRun: boolean;
  applied: boolean;
  options: { onExisting: string | null; onError: string | null };
  summary: Summary;
  rows: ResultRow[];
  confirm: ConfirmInfo | null;
}

const count = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null);
const shown = (v: unknown): string => {
  if (v === null || v === undefined) return "(empty)";
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return tidy(s ?? "", 160) || "(empty)";
};

/** Reads an import reply (200 or 422). Everything is untrusted; a reply that does not add up is null, never a guess. */
export function parseImportReply(body: unknown): ImportReply | null {
  if (!isObject(body) || typeof body.dryRun !== "boolean" || typeof body.applied !== "boolean" || !isObject(body.summary) || !Array.isArray(body.rows)) return null;
  const s = body.summary;
  const nums = ["total", "create", "update", "unchanged", "exists", "error", "skipped"].map((k) => count(s[k]));
  if (nums.some((n) => n === null)) return null;
  const [total, create, update, unchanged, exists, error, skipped] = nums as number[];
  const rows: ResultRow[] = [];
  for (const r of body.rows) {
    if (!isObject(r) || count(r.index) === null) return null;
    const action: RowAction = typeof r.action === "string" && (ACTIONS as readonly string[]).includes(r.action) ? (r.action as RowAction) : "unknown";
    rows.push({
      index: r.index as number,
      slug: tidy(r.slug, 120),
      action,
      plannedAction: typeof r.plannedAction === "string" && (ACTIONS as readonly string[]).includes(r.plannedAction) ? (r.plannedAction as RowAction) : null,
      errors: (Array.isArray(r.errors) ? r.errors : []).filter(isObject).map((e) => ({ field: tidy(e.field, 60) || "(row)", code: tidy(e.code, 60), message: safeMessage(e.message, 300) })),
      diff: isObject(r.diff) ? Object.entries(r.diff).map(([field, d]) => ({ field: tidy(field, 60), before: isObject(d) ? shown(d.before) : "(unknown)", after: isObject(d) ? shown(d.after) : "(unknown)" })) : [],
      clientId: typeof r.clientId === "string" && r.clientId !== "" ? r.clientId : null,
    });
  }
  const opts = isObject(body.options) ? body.options : {};
  const c = isObject(body.confirm) ? body.confirm : null;
  const ww = c && isObject(c.wouldWrite) ? c.wouldWrite : null;
  const confirm: ConfirmInfo | null =
    c && typeof c.wouldApply === "boolean" && typeof c.wouldAbort === "boolean" && ww && count(ww.create) !== null && count(ww.update) !== null
      ? { wouldApply: c.wouldApply, wouldAbort: c.wouldAbort, wouldWrite: { create: ww.create as number, update: ww.update as number }, message: typeof c.message === "string" ? safeMessage(c.message, 500) : null, caveats: (Array.isArray(c.caveats) ? c.caveats : []).filter((x): x is string => typeof x === "string").map((x) => safeMessage(x, 400)) }
      : null;
  return {
    dryRun: body.dryRun,
    applied: body.applied,
    options: { onExisting: typeof opts.onExisting === "string" ? opts.onExisting : null, onError: typeof opts.onError === "string" ? opts.onError : null },
    summary: { total, create, update, unchanged, exists, error, skipped },
    rows,
    confirm,
  };
}

/** A dry run reply is usable as a preview only if it really was a dry run, wrote nothing, and covers exactly the rows that were sent. */
export function isValidPreview(reply: ImportReply | null, sentRows: number): reply is ImportReply {
  return !!reply && reply.dryRun && !reply.applied && reply.summary.total === sentRows && reply.rows.length === sentRows;
}

/** A confirm reply is a success only if the server says it applied the rows. */
export function isAppliedReply(reply: ImportReply | null, sentRows: number): reply is ImportReply {
  return !!reply && !reply.dryRun && reply.applied && reply.summary.total === sentRows && reply.rows.length === sentRows;
}

// ---- the plan ---------------------------------------------------------------------------------------------------

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export interface Plan {
  /** True when Confirm may be pressed for this preview. */
  canConfirm: boolean;
  /** Why it may not (shown next to the disabled button), or null. */
  blockedReason: string | null;
  /** What Confirm will do, in words: only what the preview said. */
  willDo: string;
  /** What it will not do, or null. */
  willNot: string | null;
  /** The server's own caveats about the confirmation (current servers), e.g. a slug race that fails the whole write. */
  caveats: string[];
}

/**
 * The sentence under the Confirm button. A current server states exactly what a confirm with this body would do (`confirm`): the page
 * uses that. Older servers send no `confirm`, and then the plan is derived from the summary and the options as before.
 */
export function planFor(summary: Summary, options: ImportOptions, confirm: ConfirmInfo | null = null): Plan {
  const writes = confirm ? confirm.wouldWrite.create + confirm.wouldWrite.update : summary.create + summary.update;
  const create = confirm ? confirm.wouldWrite.create : summary.create;
  const update = confirm ? confirm.wouldWrite.update : summary.update;
  const parts: string[] = [];
  parts.push(`Create ${plural(create, "client", "clients")}`);
  parts.push(`update ${plural(update, "existing client", "existing clients")}`);
  if (summary.error > 0 && options.onError === "skip") parts.push(`skip ${plural(summary.error, "invalid row", "invalid rows")}`);
  const nots: string[] = [];
  if (summary.exists > 0) nots.push(`${plural(summary.exists, "existing client already has", "existing clients already have")} different values and will not be changed (choose "update existing clients" to change them)`);
  if (summary.unchanged > 0) nots.push(`${plural(summary.unchanged, "row is", "rows are")} identical to what is stored and will not be touched`);
  const willDo = `${parts.join(", ")}.`;
  const willNot = nots.length ? `${nots.join("; ")}.` : null;
  const caveats = confirm?.caveats ?? [];
  if (confirm ? confirm.wouldAbort : summary.error > 0 && options.onError === "abort") {
    const why = confirm?.message ?? `${plural(summary.error, "row is", "rows are")} invalid and "stop if any row is invalid" is selected, so nothing would be written.`;
    return { canConfirm: false, blockedReason: `${why} Fix the rows, or choose "skip invalid rows" and run the dry run again.`, willDo: "Nothing would be written.", willNot, caveats };
  }
  if (writes === 0) {
    return { canConfirm: false, blockedReason: "The dry run found nothing to create or update, so there is nothing to confirm.", willDo: "Nothing would be written.", willNot, caveats };
  }
  if (confirm && !confirm.wouldApply) {
    return { canConfirm: false, blockedReason: confirm.message ?? "The server says a confirmation would not write anything.", willDo: "Nothing would be written.", willNot, caveats };
  }
  return { canConfirm: true, blockedReason: null, willDo, willNot, caveats };
}

/** What a failed request means. A 4xx is a definite refusal (nothing written). A dropped connection, 5xx or unreadable 200 on the confirm is NOT known. */
export function describeFailure(status: number | null, code: string | null, serverMessage: string | null, step: "dry" | "confirm"): { kind: "refused" | "uncertain"; headline: string; detail: string | null } {
  const msg = serverMessage ? safeMessage(serverMessage, 300) : null;
  if (status === null || status >= 500) {
    return step === "dry"
      ? { kind: "refused", headline: status === null ? "Couldn't reach the server. The dry run writes nothing, so it is safe to try again." : `The server failed (HTTP ${status}). A dry run writes nothing, so it is safe to try again.`, detail: msg }
      : {
          kind: "uncertain",
          headline: status === null ? "Couldn't reach the server, so it is not known whether any rows were written." : `The server failed (HTTP ${status}), so it is not known whether any rows were written.`,
          detail: "Do not send it again blindly. Run the dry run again: a row that was already created now shows as unchanged (importing is idempotent by slug), and anything still to be created shows as will create.",
        };
  }
  if (status === 401) return { kind: "refused", headline: "The dashboard token was rejected. Nothing was written.", detail: msg };
  if (status === 403) return { kind: "refused", headline: "Not allowed. Nothing was written.", detail: msg };
  if (status === 413 || code === "TOO_MANY_ROWS") return { kind: "refused", headline: `${TOO_MANY_MESSAGE} Nothing was written.`, detail: msg };
  if (status === 409 || code === "IMPORT_CONFLICT") return { kind: "refused", headline: "Nothing was written: another client with one of these slugs was created while this import ran.", detail: `${msg ?? ""} Run the dry run again to see the current state.`.trim() };
  if (status === 422) return { kind: "refused", headline: "Nothing was written: some rows are invalid and the import stops when any row is invalid.", detail: "The per-row report below shows what to fix. Or choose \"skip invalid rows\" to import the valid ones." };
  return { kind: "refused", headline: `The server refused the request (HTTP ${status}). Nothing was written.`, detail: msg };
}
